import * as React from 'react';
import { Link } from 'react-router-dom';
import { mergeClasses, ROUTE_PATH } from '../../../constants';
import Icon from '../../../widgets/Icons';
import ScorePanel from '../../../widgets/ScorePanel';
import LoadingIndicator from '../../../widgets/LoadingIndicator';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Button } from 'reactstrap';
import { memoizedAgentSelectors } from '../../../selectors';
import { riskSelector } from '../../../reducers/risk';
import { apiRequest } from '../../../APIClient';
import { sessionSelector } from '../../../reducers/session';

const NUMBER_FORMATTER = new Intl.NumberFormat();

const AgentSummary = () => {
  const dispatch = useDispatch();
  const fetchAgents = React.useCallback(() => {
    dispatch(apiRequest.getAgentList());
  }, [dispatch]);

  const totalCount = useSelector(memoizedAgentSelectors.selectNumberOfAgents);
  const containedCount = useSelector(
    memoizedAgentSelectors.selectNumberOfContainedAgents,
  );
  const onlineCount = useSelector(
    memoizedAgentSelectors.selectNumberOfOnlineAgents,
  );
  const isLoading = useSelector(riskSelector.isLoadingAgents);
  const error = useSelector(riskSelector.getAgentsError);

  const orgID = useSelector(sessionSelector.getRiskID);
  const orgIDParam = orgID ? `?id=${orgID}` : '';

  const showContainmentStatus = containedCount > 0;

  let panelBody, panelFooter, link, isExternalLink;

  if (error) {
    panelBody = error.message;
    panelFooter = (
      <div className="text-right mt-lg-4 agent-panel-footer">
        <Button
          size="lg"
          outline
          color="primary"
          style={{ fontSize: '14px' }}
          onClick={fetchAgents}
        >
          Retry
        </Button>
      </div>
    );
  } else if (isLoading) {
    panelBody = <LoadingIndicator size="lg" />;
  } else if (totalCount > 0) {
    link = ROUTE_PATH.DASHBOARD.ENDPOINT;
    panelBody = <h1>{NUMBER_FORMATTER.format(onlineCount)}</h1>;
    panelFooter = (
      <div className="agent-panel-footer">
        The number of endpoint agents that have reported within the last 72
        hours.
      </div>
    );
  } else {
    link = ROUTE_PATH.DOWNLOADS.UNIFIED_PORTAL + orgIDParam;
    isExternalLink = true;
    panelBody = (
      <div className="score-panel-footer">
        Download the Endpoint Agent and install it on your endpoints in order to
        view their reporting and containment status.
      </div>
    );
    panelFooter = (
      <div className="text-right mt-lg-5 agent-panel-footer">
        <Button size="lg" outline color="primary" style={{ fontSize: '14px' }}>
          Go to Downloads
        </Button>
      </div>
    );
  }
  const title = showContainmentStatus ? (
    <span>
      Online Endpoints
      <Badge pill color="danger" className="ml-2 vertical-bottom">
        {containedCount}
      </Badge>
    </span>
  ) : (
    'Online Endpoints'
  );

  const containedWarning = showContainmentStatus ? (
    <div className="text-danger mt-3">
      {containedCount === 1
        ? '1 endpoint is contained'
        : `${containedCount} endpoints are contained`}
    </div>
  ) : null;

  let panel = (
    <ScorePanel
      className={mergeClasses([
        'hover-shadow',
        totalCount === 0 ? 'no-center-align' : '',
      ])}
      title={title}
      icon={<Icon.Device />}
      score={<div className="score-bar">{panelBody}</div>}
    >
      {panelFooter}
      {containedWarning}
    </ScorePanel>
  );

  if (link) {
    panel = isExternalLink ? (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="clickable-dashboard-item"
      >
        {panel}
      </a>
    ) : (
      <Link to={link} className="clickable-dashboard-item">
        {panel}
      </Link>
    );
  }

  return panel;
};

export default AgentSummary;
